import React, { useMemo, useRef } from "react";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "../../ui/tooltip";
import { Popover, PopoverTrigger, PopoverAnchor, PopoverContent } from "../../ui/popover";

// Shared "info card" visual tokens for every configurable-tooltip surface —
// keeps the hand-rolled createPortal panels this replaces (Resource
// Utilization Chart, Resource Status Monitor) and the pre-existing Radix
// Tooltip panels (Aggregation KPI, Efficiency KPI) visually identical.
// w-auto overrides Popover's own base w-72 (fixed 18rem) — this card must
// size to its content (an object table can be narrower or much wider),
// never a fixed width. Tooltip's base has no width utility, so w-auto is a
// no-op there, but keeping one shared constant for both avoids the two
// surfaces silently drifting in size behavior.
const CARD_CLASS =
  "p-0 overflow-hidden max-w-none w-auto bg-white text-gray-900 border border-[#e2e8f0] rounded-[10px] " +
  "shadow-[0_8px_24px_rgba(0,0,0,0.13),0_2px_6px_rgba(0,0,0,0.07)]";

/**
 * Hover-triggered tooltip surface. Thin wrapper over Radix Tooltip pre-wired
 * with the shared card styling. Must be nested under a <TooltipProvider>
 * (re-exported here) — one Provider per tile is enough for multiple
 * HoverInfoCard instances.
 */
export const HoverInfoCard = ({
  trigger,
  children,
  className = "",
  side,
  align,
  sideOffset = 4,
  collisionPadding = 8,
  // "card": white bordered card (object tables, multi-line info bodies).
  // "plain": the app's default dark minimal tooltip style — for a short
  // one-line hint that isn't part of the configurable-tooltip pattern.
  variant = "card",
}) => (
  <Tooltip>
    <TooltipTrigger asChild>{trigger}</TooltipTrigger>
    <TooltipContent
      side={side}
      align={align}
      sideOffset={sideOffset}
      collisionPadding={collisionPadding}
      className={variant === "card" ? `${CARD_CLASS} ${className}` : className || undefined}
    >
      {children}
    </TooltipContent>
  </Tooltip>
);

/**
 * Click-pinned tooltip surface. Thin wrapper over Radix Popover pre-wired
 * with the shared card styling — replaces hand-rolled createPortal +
 * getBoundingClientRect + viewport-clamp + outside-click-listener code with
 * Radix's built-in collision avoidance and outside-click/Escape dismissal.
 *
 * Anchor either to a real trigger element (`trigger`, rendered via
 * PopoverTrigger asChild — e.g. a hoverable slot/row) or to an arbitrary
 * point with no DOM element of its own (`anchorRect: {left, top}` — e.g. a
 * Chart.js click event, which only gives coordinates) via Radix's virtual
 * anchor. Exactly one of `trigger`/`anchorRect` should be supplied.
 *
 * `open`/`onOpenChange` are controlled by the caller so it can implement
 * "click again to toggle closed" / "hover suppressed while pinned" logic
 * identically to how the tile already tracks its own selection state.
 */
export const PinnedInfoCard = ({
  open,
  onOpenChange,
  trigger,
  anchorRect,
  children,
  className = "",
  side,
  align,
  sideOffset = 4,
  collisionPadding = 8,
}) => {
  const virtualRef = useRef({ getBoundingClientRect: () => new DOMRect(0, 0, 0, 0) });

  const virtualElement = useMemo(() => {
    if (!anchorRect) return null;
    return {
      getBoundingClientRect: () =>
        new DOMRect(anchorRect.left, anchorRect.top, anchorRect.width ?? 0, anchorRect.height ?? 0),
    };
  }, [anchorRect]);

  if (virtualElement) {
    virtualRef.current = virtualElement;
  }

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      {trigger ? (
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      ) : (
        <PopoverAnchor virtualRef={virtualRef} />
      )}
      <PopoverContent
        side={side}
        align={align}
        sideOffset={sideOffset}
        collisionPadding={collisionPadding}
        onOpenAutoFocus={(e) => e.preventDefault()}
        className={`${CARD_CLASS} ${className}`}
      >
        {children}
      </PopoverContent>
    </Popover>
  );
};

export { TooltipProvider };
